import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import PageBar from '../../components/PageBar/PageBar';
import { useOrderBy } from '../../hooks/useOrderBy';
import useTablePages from '../../hooks/useTablePages';
import TableWindow from '../../components/Table/TableWindow';
import {
  companyColumns,
  individualColumns,
  waterSupplierColumns,
} from './Jobs.columns';
import {
  GET_INBOUND_ASSIGNMENT_IDS,
  GET_INBOUND_ASSIGNMENTS_COMPANY,
  GET_INBOUND_ASSIGNMENTS_INDIVIDUAL,
  GET_INBOUND_ASSIGNMENTS_WATER_SUPPLIER,
  JobItemCompany,
} from './Jobs.queries';
import { RouteComponentProps, withRouter } from 'react-router';
import useThirdPaneCallbacks from '../../hooks/useThirdPaneCallbacks';
import ThirdPaneLayout, {
  MainColumn,
} from '../../components/Layout/ThirdPaneLayout';
import FilterBarJob, {
  defaultFilters,
  JobFilters,
  stringSearchFields,
  stringSearchFieldsCompany,
} from './FilterBarJob';
import useMode from '../../hooks/useMode';
import useColumnConfiguration from '../../hooks/useColumnConfiguration';
import useStoredFilters from '../../hooks/useStoredFilters';
import JobsActions from './JobsActions';
import { UserMode } from '../../hooks/useApolloClient';
import JobThirdPane from '../../components/Job/JobThirdPane';
import { PortalWithState } from 'react-portal';
import Button from '../../components/Button/Button';
import AddJobModal from '../../components/Job/AddJobModal';
import useRightPaneConfiguration from '../../hooks/useRightPaneConfiguration';
import SelectableMap, { MapPoint } from '../../components/Map/SelectableMap';
import { flagOptions } from '../../components/BackflowTest/BackflowTestFlagsPicker';
import { getStringSearchOr } from '../../helpers/search';
import RequirePerms from '@/components/RequirePerms';

const convertStoredTestResult = (testResult: string) => {
  let initialResultVal: string | undefined,
    finalResultVal: string | undefined = undefined;
  switch (testResult) {
    case 'FAIL':
    case 'PASS':
      finalResultVal = testResult;
      break;
    case 'FAILPASS':
      initialResultVal = 'FAIL';
      finalResultVal = 'PASS';
      break;
  }
  return {
    ['job.backflowTest.initialResult']: initialResultVal,
    ['job.backflowTest.finalResult']: finalResultVal,
  };
};

const convertFlags = (flags: string[]) => {
  const flagFilters = flagOptions
    .filter((option) => {
      return !!flags.find((value) => value === option.value);
    })
    .map((option) => {
      return Object.keys(option.filter).reduce(
        (filter, key) => {
          filter[`job.backflowTest.${key}`] = option.filter[key];
          return filter;
        },
        {} as Record<string, any>
      );
    });

  if (flagFilters.length === 0) {
    return {};
  }

  if (flagFilters.length === 1) {
    return flagFilters[0];
  }

  return {
    or: flagFilters,
  };
};

const convertStoredString = (searchString: string, mode: UserMode) => {
  const fields =
    mode === 'individual' ? stringSearchFields : stringSearchFieldsCompany;
  return { or: getStringSearchOr(fields, searchString) };
};

const convertStoredDate = (
  dateStart: string | undefined,
  dateEnd: string | undefined,
  currentDateFilterField = 'job.backflowTest.dateTested'
) => {
  return {
    [currentDateFilterField]:
      dateStart && dateEnd
        ? {
            between: [dateStart, dateEnd],
          }
        : dateStart || dateEnd
          ? {
              gte: dateStart ? dateStart : undefined,
              lte: dateEnd ? dateEnd + 'T23:59:59.999Z' : undefined,
            }
          : undefined,
  };
};

export const convertFilters = (storedFilters: JobFilters, mode: UserMode) => {
  const query: { and: Record<string, any>[] } = { and: [] };
  if (storedFilters.status) {
    query.and.push({
      status: storedFilters.status,
    });
  }
  if (storedFilters.testResult) {
    query.and.push(convertStoredTestResult(storedFilters.testResult));
  }
  if (storedFilters.stringSearch) {
    query.and.push(convertStoredString(storedFilters.stringSearch, mode));
  }
  if (storedFilters.flags) {
    query.and.push(convertFlags(storedFilters.flags));
  }
  if (
    storedFilters.currentDateFilterField !== 'ALL' &&
    (storedFilters.dateStart || storedFilters.dateEnd)
  ) {
    query.and.push(
      convertStoredDate(
        storedFilters.dateStart,
        storedFilters.dateEnd,
        storedFilters.currentDateFilterField
      )
    );
  }

  if (query.and.length === 0) {
    delete (query as any).and;
  }

  return query;
};

type JobDetailParams = {
  id?: string;
};

type JobDetailProps = RouteComponentProps<JobDetailParams>;

/**
 * @component for jobs page
 */
const Jobs: React.FC<JobDetailProps> = ({ match }) => {
  const mode: UserMode = useMode();

  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<JobFilters>(
      (filters) => convertFilters(filters, mode),
      'jobs',
      defaultFilters
    );

  const availableColumns =
    mode === 'water-supplier'
      ? waterSupplierColumns
      : mode === 'company'
        ? companyColumns
        : individualColumns;
  const { orderBy, onHeaderClick } = useOrderBy('jobs', availableColumns);

  const { rightPaneConfig, setRightPaneConfig } =
    useRightPaneConfiguration('jobs');

  const {
    loading,
    error,
    itemIds,
    idItems,
    selectedIds,
    setSelectedIds,
    itemLoader,
    refetch,
  } = useTablePages<
    JobItemCompany,
    'id',
    {
      id: string;
      job?: { address?: { latitude?: number; longitude?: number } };
    }
  >(
    'inboundAssignments',
    GET_INBOUND_ASSIGNMENT_IDS,
    {
      where: queryFilters,
      limit: null,
      orderBy,
      includeAddress: rightPaneConfig.visible,
    },
    mode === 'company'
      ? GET_INBOUND_ASSIGNMENTS_COMPANY
      : mode === 'water-supplier'
        ? GET_INBOUND_ASSIGNMENTS_WATER_SUPPLIER
        : GET_INBOUND_ASSIGNMENTS_INDIVIDUAL,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
      orderBy,
    }),
    'id'
  );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'jobs');

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(`/${useMode()}/jobs`, itemIds, match.params.id, 'id');

  const points: MapPoint[] = useMemo(() => {
    return rightPaneConfig.visible
      ? idItems
          .filter(
            (item) =>
              typeof item.job?.address?.longitude === 'number' &&
              typeof item.job?.address?.latitude === 'number'
          )
          .map((item) => ({
            id: item.id,
            coordinates: [
              item.job?.address?.longitude as number,
              item.job?.address?.latitude as number,
            ],
          }))
      : [];
  }, [idItems]);

  return (
    <ThirdPaneLayout paneVisible={!!match.params.id}>
      <MainColumn px="mainContent" pb="mainContent">
        <PageBar title="Backflow Test Jobs">
          <RequirePerms jobs="CREATE">
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  {(mode === 'company' || mode === 'water-supplier') && (
                    <Button
                      ml={3}
                      variant="primary"
                      icon="plus"
                      onClick={openPortal}
                    >
                      Add Backflow Test
                    </Button>
                  )}
                  {portal(
                    <AddJobModal
                      onClose={() => {
                        closePortal();
                        refetch();
                      }}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          </RequirePerms>
        </PageBar>
        <FilterBarJob
          filters={storedFilters}
          setFilters={setStoredFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            emptyMessage="No jobs found."
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            actionItems={
              <JobsActions
                reload={refetch}
                filters={storedFilters}
                assignmentIds={selectedIds}
              />
            }
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
            rightPane={
              <SelectableMap
                points={points}
                onPointClick={onRowClick}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                flexBasis={rightPaneConfig.width}
              />
            }
            rightPaneConfig={rightPaneConfig}
            setRightPaneConfig={setRightPaneConfig}
            rightPaneIcon="map"
          />
        )}
      </MainColumn>
      <JobThirdPane
        key={match.params.id}
        assignmentId={match.params.id}
        onPrev={prev}
        prevEnabled={prevEnabled}
        onNext={next}
        nextEnabled={nextEnabled}
        onToggle={closeThirdPane}
        reload={refetch}
      />
    </ThirdPaneLayout>
  );
};

Jobs.propTypes = {
  match: PropTypes.any,
};

export default withRouter(Jobs);
